const MAX_INPUT_SIZE = 5 * 1024 * 1024; // 5 MB
const MAX_OUTPUT_BYTES = 125000; // Match existing 125 KB limit per image
const JPEG_QUALITY_START = 0.85;
const JPEG_QUALITY_MIN = 0.4;
const JPEG_QUALITY_STEP = 0.1;

/**
 * Compresses an image file: resizes to fit maxDimension, encodes as JPEG
 * (with quality stepping to stay under MAX_OUTPUT_BYTES), and returns the
 * full data-URI string for direct use in <img src=...>.
 *
 * PNG files with transparency are kept as PNG; everything else becomes JPEG
 * for dramatically smaller payloads.
 *
 * Backward-compat: callers that previously stored raw base64 can use
 * imageDataUri() at render time to handle both old and new formats.
 *
 * @param {File} file
 * @param {number} [maxDimension=200]
 * @returns {Promise<string>} Full data-URI, e.g. "data:image/jpeg;base64,/9j..."
 */
export function compressImage(file, maxDimension = 200) {
    return new Promise((resolve, reject) => {
        if (!file) {
            reject(new Error("No file provided"));
            return;
        }

        if (file.size > MAX_INPUT_SIZE) {
            reject(new Error("File exceeds 5 MB limit. Please choose a smaller image."));
            return;
        }

        const img = new Image();
        const url = URL.createObjectURL(file);

        img.onload = () => {
            URL.revokeObjectURL(url);

            let {width, height} = img;

            if (width > maxDimension || height > maxDimension) {
                if (width > height) {
                    height = Math.round(height * (maxDimension / width));
                    width = maxDimension;
                } else {
                    width = Math.round(width * (maxDimension / height));
                    height = maxDimension;
                }
            }

            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);

            // Use PNG only for files that might have transparency
            const isPng = file.type === "image/png";
            if (isPng && hasTransparency(ctx, width, height)) {
                resolve(canvas.toDataURL("image/png"));
                return;
            }

            // JPEG — step down quality until under size limit
            let quality = JPEG_QUALITY_START;
            let dataUrl;
            do {
                dataUrl = canvas.toDataURL("image/jpeg", quality);
                quality -= JPEG_QUALITY_STEP;
            } while (dataUriByteSize(dataUrl) > MAX_OUTPUT_BYTES && quality >= JPEG_QUALITY_MIN);

            resolve(dataUrl);
        };

        img.onerror = () => {
            URL.revokeObjectURL(url);
            reject(new Error("Failed to load image. Please try a different file."));
        };

        img.src = url;
    });
}

/**
 * Returns a usable <img> src for a metadata image value.
 * - New format (data-URI): returned as-is
 * - Legacy format (raw base64): wrapped with data:image/png;base64,
 */
export function imageDataUri(value) {
    if (!value) return "";
    if (value.startsWith("data:")) return value;
    return `data:image/png;base64,${value}`;
}

// --- internal helpers ---

function hasTransparency(ctx, w, h) {
    const data = ctx.getImageData(0, 0, w, h).data;
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) return true;
    }
    return false;
}

function dataUriByteSize(dataUri) {
    // base64 portion is after the comma
    const base64 = dataUri.split(",")[1] || "";
    return Math.ceil(base64.length * 3 / 4);
}
